import React, { useEffect, useState } from 'react';
import SEO from './SEO';
import OptimizedImage from './OptimizedImage';
import WhatsAppFloat from './WhatsAppFloat';
import { trackCTA } from '../utils/analytics';
import { getSegmentLandingPageSchema } from '../utils/structuredData';
import { modulos } from '../data/modulos';
import './LandingPage.css';

const WHATSAPP_NUMBER = '5543996782197';
const DEMO_VIDEO_URL = '';

const getWhatsAppUrl = (message = '') => {
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${WHATSAPP_NUMBER}${message ? `?text=${encodedMessage}` : ''}`;
};

const ADS_EVENTS = {
  WHATSAPP_HEADER: 'ads_conv_lp_servicos_whatsapp_header',
  WHATSAPP_CTA_FINAL: 'ads_conv_lp_servicos_whatsapp_cta_final',
  DOWNLOAD_CTA_FINAL: 'ads_conv_lp_servicos_download_cta_final',
  DOWNLOAD_FOOTER: 'ads_conv_lp_servicos_download_footer'
};

const LandingServicos = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const servicosModulo = modulos.servicos;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleWhatsAppClick = (e, message, eventName) => {
    e.preventDefault();
    const whatsappUrl = getWhatsAppUrl(message);
    if (window.gtagSendEvent) {
      window.gtagSendEvent(whatsappUrl, eventName);
    } else {
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownloadClick = (e, url, eventName) => {
    e.preventDefault();
    if (window.gtagSendEvent) {
      window.gtagSendEvent(url, eventName);
    } else {
      window.location.href = url;
    }
  };

  return (
    <div className="landing-page">
      <SEO
        title="ERP para Serviços e Assistência Técnica | Veritux ERP"
        description="Sistema para empresas de serviços e manutenção com controle de orçamento, ordem de serviço e faturamento. Organize o fluxo técnico e administrativo."
        keywords="ERP para serviços, sistema ordem de serviço, software para assistência técnica, orçamento de serviço, controle de manutenção"
        url="https://www.veritux.com.br/erp-para-servicos"
        structuredData={getSegmentLandingPageSchema('servicos', 'https://www.veritux.com.br/erp-para-servicos')}
      />

      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <a href="/" className="logo">
              <img src="/Logo.ico" alt="Veritux ERP" className="logo-img" />
              <span className="logo-text">Veritux ERP</span>
            </a>
            <nav className="nav" aria-label="Navegação principal">
              <a href="#beneficios">Benefícios</a>
              <a href="#video">Demonstração</a>
              <a href="#faq">FAQ</a>
            </nav>
            <div className="header-cta">
              <a
                href={getWhatsAppUrl('Olá! Quero ver o Veritux ERP para serviços com foco em ordem de serviço.')}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                onClick={(e) => {
                  trackCTA('whatsapp_servicos', 'header');
                  handleWhatsAppClick(e, 'Olá! Quero ver o Veritux ERP para serviços com foco em ordem de serviço.', ADS_EVENTS.WHATSAPP_HEADER);
                }}
              >
                Falar com especialista
              </a>
            </div>
            <button
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Abrir menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </div>
      </header>

      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}></div>
      <nav className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`} aria-label="Menu mobile">
        <div className="mobile-menu-content">
          <a href="#beneficios" onClick={() => setIsMobileMenuOpen(false)} className="mobile-menu-link">Benefícios</a>
          <a href="#video" onClick={() => setIsMobileMenuOpen(false)} className="mobile-menu-link">Demonstração</a>
          <a href="#faq" onClick={() => setIsMobileMenuOpen(false)} className="mobile-menu-link">FAQ</a>
        </div>
      </nav>

      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                ERP para serviços com <span className="highlight">ordem de serviço</span> e <span className="highlight">orçamento rastreável</span>
              </h1>
              <p className="hero-subtitle">
                Organize o fluxo do atendimento técnico da abertura até o faturamento.
              </p>
              <p className="hero-description">
                O Veritux estrutura seu processo de orçamento, ordem de serviço, execução e faturamento
                para reduzir perda de informação e dar previsibilidade para o time.
              </p>
              <div className="hero-cta">
                <a href="#demo" className="btn btn-primary btn-large">Quero ver o fluxo de OS</a>
                <a href="#video" className="btn btn-secondary btn-large">Ver demo (serviços)</a>
              </div>
              <div className="hero-trust">
                <p className="trust-text">✓ Orçamento com conversão em OS</p>
                <p className="trust-text">✓ Histórico técnico centralizado</p>
                <p className="trust-text">✓ Faturamento de serviço no mesmo fluxo</p>
              </div>
            </div>
            <div className="hero-visual">
              <OptimizedImage
                src="/images/product/hero/dashboard"
                alt="Tela de gestão para serviços e manutenção com ordem de serviço"
                className="hero-dashboard-image"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      <section id="beneficios" className="benefits">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Fluxo operacional para assistência e manutenção</h2>
            <p className="section-subtitle">Menos perda de contexto entre equipe técnica e administrativo</p>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">🧾</div>
              <h3 className="benefit-title">Orçamento de serviço com contexto técnico</h3>
              <p className="benefit-text">{servicosModulo.funcionalidades.find((f) => f.nome === 'Orçamento de Serviço').descricao}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🔧</div>
              <h3 className="benefit-title">Ordem de serviço por status e histórico</h3>
              <p className="benefit-text">{servicosModulo.detalhes.ordemServico}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">💼</div>
              <h3 className="benefit-title">Faturamento e financeiro no mesmo fluxo</h3>
              <p className="benefit-text">{servicosModulo.detalhes.faturamento}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="video" className="video-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Demonstração de OS e Orçamentos</h2>
            <p className="section-subtitle">Espaço preparado para vídeo do fluxo técnico</p>
          </div>
          <div className="video-container">
            {DEMO_VIDEO_URL ? (
              <div className="video-wrapper">
                <iframe
                  className="youtube-player"
                  src={DEMO_VIDEO_URL}
                  title="Demonstração do Veritux ERP para serviços"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="segment-demo-placeholder">
                <h3>Demonstração OS e Orçamentos (em breve)</h3>
                <p>Substitua este bloco pelo vídeo com o fluxo real: orçamento, OS, execução e faturamento.</p>
                <a
                  href={getWhatsAppUrl('Olá! Quero agendar uma demonstração do fluxo de OS e orçamento para serviços.')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  onClick={(e) => handleWhatsAppClick(e, 'Olá! Quero agendar uma demonstração do fluxo de OS e orçamento para serviços.', ADS_EVENTS.WHATSAPP_HEADER)}
                >
                  Agendar demonstração guiada
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="social-proof">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Programa Pioneiro — 20 vagas</h2>
            <p className="section-subtitle">
              O Veritux está abrindo as primeiras vagas para empresas de serviços. Teste 15 dias grátis e entre
              com condição especial de Pioneiro para os 20 primeiros clientes.
            </p>
          </div>
          <p className="stats-footnote">
            Os resultados dependem do uso e do processo de cada empresa. Quando tivermos clientes Pioneiros,
            traremos depoimentos reais e autorizados por escrito.
          </p>
        </div>
      </section>

      <section id="faq" className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Perguntas frequentes de serviços</h2>
          </div>
          <div className="faq-grid">
            <div className="faq-item">
              <h3 className="faq-question">Consigo acompanhar a OS por status?</h3>
              <p className="faq-answer">Sim. O fluxo permite acompanhar etapa, responsável e histórico por ordem de serviço.</p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">O orçamento pode virar OS sem retrabalho?</h3>
              <p className="faq-answer">Sim. O processo de orçamento de serviço suporta conversão para ordem de serviço no mesmo contexto.</p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Atende assistência técnica com equipamentos?</h3>
              <p className="faq-answer">Sim. O módulo de serviços permite controle de atendimentos e histórico técnico vinculado ao cliente/serviço.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="demo" className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Quer avaliar o Veritux para sua equipe técnica?</h2>
            <p className="cta-subtitle">
              Teste grátis por 15 dias, sem cartão. Entre no <strong>Programa Pioneiro</strong>: R$ 49,90/mês
              nos 6 primeiros meses (depois R$ 197/mês, congelado por 12 meses) — limitado às 20 primeiras contratações.
            </p>
            <div className="cta-buttons">
              <a
                href={getWhatsAppUrl('Olá! Quero ver uma demonstração do Veritux para serviços com foco em OS.')}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-large"
                onClick={(e) => {
                  trackCTA('whatsapp_demo_servicos', 'cta_final');
                  handleWhatsAppClick(e, 'Olá! Quero ver uma demonstração do Veritux para serviços com foco em OS.', ADS_EVENTS.WHATSAPP_CTA_FINAL);
                }}
              >
                Agendar conversa no WhatsApp
              </a>
              <a
                href="/downloads"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-large"
                onClick={(e) => {
                  trackCTA('download_instalador_servicos', 'cta_final');
                  handleDownloadClick(e, '/downloads', ADS_EVENTS.DOWNLOAD_CTA_FINAL);
                }}
              >
                Baixar instalador e começar a usar
              </a>
            </div>
            <p className="segment-switch-link">
              Você opera loja e PDV no balcão? <a href="/erp-para-comercio">Veja a landing de comércio</a>.
            </p>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-bottom">
            <p className="footer-copyright">
              Desenvolvido por <a href="https://mngd.com.br" target="_blank" rel="noopener noreferrer">MNGD Sistemas</a> &copy; 2026 Veritux ERP.
            </p>
            <div className="footer-legal">
              <a href="/funcionalidades">Funcionalidades</a>
              <span className="footer-separator">•</span>
              <a href="/erp-para-comercio">Landing comércio</a>
              <span className="footer-separator">•</span>
              <a
                href="/downloads"
                onClick={(e) => handleDownloadClick(e, '/downloads', ADS_EVENTS.DOWNLOAD_FOOTER)}
              >
                Downloads
              </a>
            </div>
          </div>
        </div>
      </footer>

      <WhatsAppFloat />
    </div>
  );
};

export default LandingServicos;
